// Shopify setup without the app: the custom pixel a merchant pastes into Shopify admin
// (Settings → Customer events → Add custom pixel), filled in with the project's publishable key.
// The script itself is generated from the app pixel's source (shopifyCustomPixel.generated.ts).

import { SHOPIFY_CUSTOM_PIXEL_TEMPLATE } from './shopifyCustomPixel.generated'

const KEY_PLACEHOLDER = '__YOM_PUBLISHABLE_KEY__'
const INGEST_PLACEHOLDER = '__YOM_INGEST_URL__'

/** A publishable (ingest-only) key: `yom_pk_live_…` / `yom_pk_test_…`. Never a `yom_sk_` key. */
export function isPublishableKey(key: string): boolean {
    return /^yom_pk_[A-Za-z0-9_]{8,120}$/.test(key.trim())
}

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]'])

/**
 * The ingest origin the pixel posts to (`/v2/collect` is appended by the pixel), or null.
 * https only; plain http is allowed for a local development host.
 */
export function ingestOrigin(url: string): string | null {
    try {
        const u = new URL(url)
        const ok =
            u.protocol === 'https:' || (u.protocol === 'http:' && LOCAL_HOSTS.has(u.hostname))
        return ok ? u.origin : null
    } catch {
        return null
    }
}

/**
 * The paste-able custom pixel for one project. Throws unless `key` is a publishable key, so a
 * secret key can never end up in a storefront script.
 */
export function shopifyCustomPixelScript(key: string, ingestUrl: string): string {
    const pk = key.trim()
    if (!isPublishableKey(pk)) {
        throw new Error('A Shopify custom pixel takes a publishable key (yom_pk_…) only')
    }
    const origin = ingestOrigin(ingestUrl)
    if (!origin) {
        throw new Error(
            'Ingest URL must be an https URL (http only on a local host: localhost, 127.0.0.1, [::1])',
        )
    }
    // Both values sit inside string literals of the template and match [A-Za-z0-9_:/.-] only.
    return SHOPIFY_CUSTOM_PIXEL_TEMPLATE.split(KEY_PLACEHOLDER)
        .join(pk)
        .split(INGEST_PLACEHOLDER)
        .join(origin)
}
